import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TablePagination,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { apiConstants, getGeneric, postGeneric, postMultipart } from '../../common/constants';
import { FormProvider, RHFTextField, RHFFileField } from '../../common/hook-form';
import RHFTextArea from '../../common/hook-form/RHFTextArea';
import { MerchantFilterCached } from '../../common/ProshipFormComponents';
import ImageCropDialog from './ImageCropDialog';

// Keys mirror com.prozo.proship.enums.TrackingTemplateEnum on the backend.
const TEMPLATE_CARDS = [
  { value: 'CLASSIC', label: 'Classic' },
  { value: 'MODERN', label: 'Modern' },
  { value: 'MINIMAL', label: 'Minimal' },
];

// Origin where tracking-app (the renderer) is served — its /preview route renders a chosen
// template with branding via query params + sample shipment data. Defaults to the dev deployment;
// override with REACT_APP_TRACKING_APP_URL for a local tracking-app.
const TRACKING_APP_URL = process.env.REACT_APP_TRACKING_APP_URL || 'https://dev.d2lfb0u0mchb6n.amplifyapp.com';

// Parent domain for assigned tracking subdomains (mirrors the subdomain-setup lambda).
const TRACKING_DOMAIN = 'proship.in';

// Logical viewport widths for the device preview (tracking-app's useIsMobile is <=900).
const DESKTOP_W = 1280;
const MOBILE_W = 390;


// ── Local copies of the merchant panel's design tokens (this admin app has no shared
//    styles/foundation module) — keep visually in sync with proship-web TrackingConfig.js. ──
const FONT = 'Poppins, sans-serif';
const NAVY = '#212B36';
const SUBTLE = '#637381';
const BORDERC = '#DFE3E8';
const PRIMARY_BLUE = '#3783e7';
const SHADOW_CARD = '0 2px 8px rgba(0,0,0,0.06)';
const SHADOW_HOVER = '0 8px 24px rgba(0,0,0,0.10)';
const sectionCardSx = { p: 3, borderRadius: '14px', border: `1px solid ${BORDERC}`, bgcolor: '#fff', mb: 2.5 };
const cardTitleSx = { fontFamily: FONT, fontWeight: 700, fontSize: '1.12rem', color: NAVY, mb: 0 };
const sectionHeaderSx = { display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 };
const numBadgeSx = (color) => ({
  width: 24, height: 24, borderRadius: '50%', bgcolor: color, color: '#fff',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 11, fontWeight: 700, flexShrink: 0, fontFamily: FONT,
});
const hintSx = { fontFamily: FONT, fontSize: '0.75rem', color: SUBTLE, lineHeight: 1.5 };
const primaryBtnSx = {
  fontFamily: FONT, fontWeight: 600, fontSize: '0.82rem', textTransform: 'none', whiteSpace: 'nowrap',
  bgcolor: PRIMARY_BLUE, color: '#fff', px: 2, py: 0.75, borderRadius: '6px',
  '&:hover': { bgcolor: PRIMARY_BLUE, filter: 'brightness(0.9)' },
};
const secondaryBtnSx = {
  fontFamily: FONT, fontWeight: 600, fontSize: '0.82rem', textTransform: 'none', whiteSpace: 'nowrap',
  color: NAVY, border: `1px solid ${BORDERC}`, px: 2, py: 0.75, borderRadius: '6px',
  '&:hover': { borderColor: PRIMARY_BLUE, bgcolor: `${PRIMARY_BLUE}08` },
};
const requiredTagSx = { fontFamily: FONT, fontWeight: 600, fontSize: 10, px: 1, py: 0.25, borderRadius: '6px', bgcolor: `${PRIMARY_BLUE}18`, color: PRIMARY_BLUE, display: 'inline-block', lineHeight: 1.6 };
const optionalTagSx = { fontFamily: FONT, fontWeight: 600, fontSize: 10, px: 1, py: 0.25, borderRadius: '6px', bgcolor: `${SUBTLE}18`, color: SUBTLE, display: 'inline-block', lineHeight: 1.6 };

// Compact, constant-height "current image" strip above each upload box — same height whether an
// image exists yet or not, so the three upload columns always line up (mirrors the merchant panel).
const UploadedThumb = ({ label, url, height = 36, onRemove }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, height: height + 8 }}>
    {url ? (
      <>
        <img src={url} alt={label} style={{ maxHeight: height, maxWidth: 140, objectFit: 'contain' }} />
        <Typography sx={hintSx}>{label}</Typography>
        {onRemove && (
          <Button size="small" color="error" sx={{ minWidth: 0, fontSize: 11, textTransform: 'none' }} onClick={onRemove}>
            Remove
          </Button>
        )}
      </>
    ) : (
      <Typography sx={hintSx}>No image uploaded yet</Typography>
    )}
  </Box>
);

const DEFAULTS = {
  merchant: '',
  designTemplate: 'CLASSIC',
  brandName: '',
  bannerBgColor: '',
  webUrl: '',
  footer: '',
  instaUrl: '',
  fbUrl: '',
  twitterUrl: '',
  linkedinUrl: '',
  whatsappNumber: '',
  termsAndConditions: '',
  returnPolicy: '',
  privacyPolicy: '',
};

// Suggest "<brand>.proship.in" from the brand website (KAM can edit before assigning).
function suggestSubdomain(webUrl) {
  if (!webUrl) return '';
  let h = String(webUrl).trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
  h = h.replace(/[/?#].*$/, '');
  const label = h.split('.')[0];
  return label ? `${label}.${TRACKING_DOMAIN}` : '';
}

/**
 * Admin/KAM DIY tracking-page config — mirrors the merchant self-serve page
 * (proship-web TrackingConfig) plus admin-only pieces: a merchant picker, the
 * tracking-page list, and the assign-URL action (instant LIVE — *.proship.in is wildcarded).
 *
 * Logo + banner images upload to VERSIONED S3 keys (tracking/{merchantOid}/logo|banner-desktop|banner-mobile-<ts>.<ext>)
 * served permanently via the wms-logos CloudFront — each upload is a fresh, fully cacheable URL.
 */
export default function TrackingPageAdmin() {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const [trackingUrl, setTrackingUrl] = useState(null);
  const [status, setStatus] = useState(null);
  const [statusReason, setStatusReason] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [bannerLargeUrl, setBannerLargeUrl] = useState('');
  const [bannerSmallUrl, setBannerSmallUrl] = useState('');
  const [uploading, setUploading] = useState({ logo: false, large: false, small: false });
  const [assignInput, setAssignInput] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [list, setList] = useState([]);
  const [listPage, setListPage] = useState(0);
  const [listSearch, setListSearch] = useState('');
  const LIST_ROWS_PER_PAGE = 10;
  const [previewSrc, setPreviewSrc] = useState('');
  const [device, setDevice] = useState('desktop');
  const previewWrapRef = useRef(null);
  const [wrapW, setWrapW] = useState(0);

  const [managedOid, setManagedOid] = useState('');
  const methods = useForm({ defaultValues: DEFAULTS });
  const { handleSubmit, watch, setValue } = methods;
  // The picker writes the RHF 'merchant' field; the list's "Manage" sets the oid directly (works even
  // when the cached merchant list has no matching option). managedOid is the page's source of truth.
  const pickerValue = watch('merchant');
  const merchantOid = managedOid;
  const designTemplate = watch('designTemplate');

  useEffect(() => {
    if (pickerValue && pickerValue !== managedOid) setManagedOid(pickerValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerValue]);
  const logoFile = watch('file');
  const bannerDesktopFile = watch('bannerDesktopFile');
  const bannerMobileFile = watch('bannerMobileFile');

  // VERSIONED filename per upload: the wms-logos CloudFront's cache policy ignores query strings,
  // so a constant key + ?v= buster would serve a stale image for up to a day after a re-upload.
  // A fresh key per upload is instantly visible and permanently cacheable (old versions orphan in S3).
  const uploadAsset = async (file, baseName) => {
    // Only the EXTENSION survives from the user's filename (sanitized); the key itself is always
    // our own base name, so identically-named source files can never collide across the three slots.
    const ext = ((file.name.split('.').pop() || 'png').toLowerCase().replace(/^jpeg$/, 'jpg').replace(/[^a-z0-9]/g, '') || 'png');
    const renamed = new File([file], `${baseName}-${Date.now()}.${ext}`, { type: file.type || 'image/png' });
    const fd = new FormData();
    fd.append('attachments[]', renamed);
    fd.append('folder', `tracking/${merchantOid}`);
    const up = await postMultipart(apiConstants.UPLOAD_LOGO_IMAGE, fd);
    const clean = up && up.data && up.data[0];
    if (!clean) throw new Error('upload returned no url');
    return clean;
  };

  // Crop-on-select: picking a file opens the crop dialog (fixed proportions per slot, anchored to
  // the live templates: desktop banner 6:1 like the real brand strips, mobile banner 3:2, logo free
  // trim); the CROPPED image is what gets uploaded, so the stored asset always fits the placeholder
  // UI. Upload still happens before Save so the cross-origin preview iframe can show a hosted URL.
  const [pendingCrop, setPendingCrop] = useState(null); // { slot, file }
  const SLOTS = {
    logo: { field: 'file', baseName: 'logo', aspect: undefined, outWidth: 600, minWidth: 120, minHeight: 48, setUrl: setLogoUrl, title: 'Crop logo', hint: 'Trim the empty space around the logo — any shape works; pages scale it by height. Recommended 360×120px or larger.' },
    large: { field: 'bannerDesktopFile', baseName: 'banner-desktop', aspect: 6, outWidth: 1920, minWidth: 960, minHeight: 160, setUrl: setBannerLargeUrl, title: 'Crop desktop banner (6:1)', hint: 'A wide strip shown across the top of the page on desktop — drag to frame the right part. Recommended 1920×320px.' },
    small: { field: 'bannerMobileFile', baseName: 'banner-mobile', aspect: 2, outWidth: 750, minWidth: 375, minHeight: 188, setUrl: setBannerSmallUrl, title: 'Crop mobile banner (2:1)', hint: 'Shown at the top of the page on phones — drag to frame the right part. Recommended 750×375px.' },
  };
  const useCropOnSelect = (file, slot) => {
    useEffect(() => {
      const f = file && file[0];
      if (!f || !merchantOid) return;
      setPendingCrop({ slot, file: f });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file]);
  };
  useCropOnSelect(logoFile, 'logo');
  useCropOnSelect(bannerDesktopFile, 'large');
  useCropOnSelect(bannerMobileFile, 'small');

  const closeCrop = () => {
    if (pendingCrop) setValue(SLOTS[pendingCrop.slot].field, null);
    setPendingCrop(null);
  };
  const onCropped = (blob) => {
    const slot = SLOTS[pendingCrop.slot];
    const key = pendingCrop.slot;
    const ext = blob.type === 'image/png' ? 'png' : 'jpg';
    const cropped = new File([blob], `crop.${ext}`, { type: blob.type });
    setPendingCrop(null);
    setValue(slot.field, null);
    setUploading((u) => ({ ...u, [key]: true }));
    uploadAsset(cropped, slot.baseName)
      .then((url) => slot.setUrl(url))
      .catch(() => enqueueSnackbar(`${slot.baseName} upload failed — please try another image.`, { variant: 'warning' }))
      .finally(() => setUploading((u) => ({ ...u, [key]: false })));
  };

  // Build the tracking-app preview URL from the current form values + uploaded image URLs.
  const buildPreviewUrl = () => {
    const v = methods.getValues();
    const qs = new URLSearchParams({
      template: v.designTemplate || 'CLASSIC',
      brandName: v.brandName || '',
      logo: logoUrl || '',
      bannerLarge: bannerLargeUrl || '',
      bannerSmall: bannerSmallUrl || '',
      webUrl: v.webUrl || '',
      color: v.bannerBgColor || '',
      footer: v.footer || '',
      instaUrl: v.instaUrl || '',
      fbUrl: v.fbUrl || '',
      twitterUrl: v.twitterUrl || '',
      linkedinUrl: v.linkedinUrl || '',
      whatsappNumber: v.whatsappNumber || '',
      termsAndConditions: v.termsAndConditions || '',
      returnPolicy: v.returnPolicy || '',
      privacyPolicy: v.privacyPolicy || '',
    });
    return `${TRACKING_APP_URL}/preview?${qs.toString()}`;
  };

  // Refresh the preview when the design or any uploaded image changes; use the button for text edits.
  useEffect(() => {
    setPreviewSrc(buildPreviewUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designTemplate, logoUrl, bannerLargeUrl, bannerSmallUrl]);

  // Measure the preview container so the desktop frame can be scaled down to fit.
  // Depends on merchantOid: the whole preview section is rendered only AFTER a merchant is picked,
  // so at mount the ref is still null — a run-once effect would never attach the observer and the
  // iframe would stay at the 0.6 fallback scale instead of fitting the container.
  useEffect(() => {
    const el = previewWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => setWrapW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [merchantOid]);

  // Load the picked merchant's config into the form (admin acts on behalf of a merchant).
  useEffect(() => {
    if (!merchantOid) return;
    setLoading(true);
    getGeneric(apiConstants.TRACKING_MY_CONFIG, { merchantOid })
      .then((res) => {
        const cfg = res && res.data;
        const pages = cfg && cfg.pages ? cfg.pages : [];
        const trackingPage = pages.find((p) => p.type === 'TRACKING') || {};
        setLogoUrl((cfg && cfg.logoUrl) || '');
        setBannerLargeUrl(trackingPage.bannerLarge || '');
        setBannerSmallUrl(trackingPage.bannerSmall || '');
        setTrackingUrl(trackingPage.url || null);
        setStatus(trackingPage.status || null);
        setStatusReason(trackingPage.statusReason || null);
        // Prefill assign field: keep an existing URL, else suggest <brand>.proship.in from the website.
        setAssignInput(trackingPage.url || suggestSubdomain(cfg && cfg.webUrl));
        setValue('designTemplate', trackingPage.designTemplate || 'CLASSIC');
        setValue('footer', trackingPage.footer || '');
        setValue('brandName', (cfg && cfg.brandName) || '');
        setValue('bannerBgColor', (cfg && cfg.bannerBgColor) || '');
        setValue('webUrl', (cfg && cfg.webUrl) || '');
        setValue('instaUrl', (cfg && cfg.instaUrl) || '');
        setValue('fbUrl', (cfg && cfg.fbUrl) || '');
        setValue('twitterUrl', (cfg && cfg.twitterUrl) || '');
        setValue('linkedinUrl', (cfg && cfg.linkedinUrl) || '');
        setValue('whatsappNumber', (cfg && cfg.whatsappNumber) || '');
        setValue('termsAndConditions', (cfg && cfg.termsAndConditions) || '');
        setValue('returnPolicy', (cfg && cfg.returnPolicy) || '');
        setValue('privacyPolicy', (cfg && cfg.privacyPolicy) || '');
        setLoading(false);
      })
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantOid, reload, setValue]);

  useEffect(() => {
    getGeneric(apiConstants.TRACKING_LIST)
      .then((res) => { setList((res && res.data) || []); setListPage(0); })
      .catch(() => setList([]));
  }, [reload]);

  const onSubmit = async (data) => {
    if (!merchantOid) {
      enqueueSnackbar('Select a merchant first', { variant: 'warning' });
      return;
    }
    // Minimum brand identity: templates fall back to the name when no logo is uploaded,
    // and the backend refuses to assign a URL without it.
    if (!data.brandName || !data.brandName.trim()) {
      enqueueSnackbar('Brand name is required', { variant: 'warning' });
      return;
    }
    // Saving a LIVE page changes what customers see immediately (no approval step) — confirm.
    if (status === 'LIVE') {
      // eslint-disable-next-line no-alert
      if (!window.confirm("This brand's tracking page is LIVE — saving will update the live page for customers immediately. Continue?")) {
        return;
      }
    }
    setLoading(true);
    try {
      const payload = {
        brandName: data.brandName,
        logoUrl,
        bannerBgColor: data.bannerBgColor,
        webUrl: data.webUrl,
        instaUrl: data.instaUrl,
        fbUrl: data.fbUrl,
        twitterUrl: data.twitterUrl,
        linkedinUrl: data.linkedinUrl,
        whatsappNumber: data.whatsappNumber,
        termsAndConditions: data.termsAndConditions,
        returnPolicy: data.returnPolicy,
        privacyPolicy: data.privacyPolicy,
        designTemplate: data.designTemplate,
        footer: data.footer,
        bannerLarge: bannerLargeUrl,
        bannerSmall: bannerSmallUrl,
      };
      await postGeneric(`${apiConstants.TRACKING_SAVE_BRANDING}?merchantOid=${merchantOid}`, payload);
      enqueueSnackbar('Tracking branding saved', { variant: 'success' });
      setReload((r) => r + 1);
    } catch (e) {
      enqueueSnackbar('Could not save. Please try again.', { variant: 'error' });
    }
    setLoading(false);
  };

  const onAssignUrl = async () => {
    if (!merchantOid) {
      enqueueSnackbar('Select a merchant first', { variant: 'warning' });
      return;
    }
    if (!assignInput || !assignInput.trim()) {
      enqueueSnackbar('Enter a tracking URL', { variant: 'warning' });
      return;
    }
    setAssigning(true);
    try {
      await postGeneric(
        `${apiConstants.TRACKING_ASSIGN_URL}?merchantOid=${merchantOid}&trackingUrl=${encodeURIComponent(assignInput.trim())}`,
        {},
      );
      enqueueSnackbar('Tracking URL assigned — the page is live (*.proship.in wildcard).', { variant: 'success' });
      setReload((r) => r + 1);
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.message) || 'Could not assign URL';
      enqueueSnackbar(msg, { variant: 'error' });
    }
    setAssigning(false);
  };

  // Kill switch: unpublish (lambda stops serving the page → visitors see default/legacy branding;
  // the URL assignment is preserved) / re-publish. Status flips synchronously on the backend.
  const [togglingLive, setTogglingLive] = useState(false);
  const onToggleLive = async (live) => {
    const question = live
      ? 'Publish this tracking page again? Customers will see the branded page immediately.'
      : 'Take this tracking page down? Customers will see the default page until it is published again.';
    // eslint-disable-next-line no-alert
    if (!window.confirm(question)) return;
    setTogglingLive(true);
    try {
      await postGeneric(`${apiConstants.TRACKING_SET_LIVE}?merchantOid=${merchantOid}&live=${live}`, {});
      enqueueSnackbar(live ? 'Page published — live again.' : 'Page taken down — visitors now see the default page.', { variant: 'success' });
      setReload((r) => r + 1);
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.message) || 'Could not update page status';
      enqueueSnackbar(msg, { variant: 'error' });
    }
    setTogglingLive(false);
  };

  const anyUploading = uploading.logo || uploading.large || uploading.small;
  // Reachable URL for the saved subdomain. The ENVIRONMENT decides the form, not the status:
  // - prod (VANITY_ENABLED=true): https://<brand>.proship.in — the wildcard routes it to the prod
  //   tracking-app, and the ?host= override is compiled OUT of prod builds anyway.
  // - staging/qa (default false): the dev tracking-app + ?host= override — the vanity URL would hit
  //   the PROD tracking-app (via the wildcard), which doesn't serve staging configs.
  const VANITY_ENABLED = process.env.REACT_APP_TRACKING_VANITY_ENABLED === 'true';
  const publicTrackingUrl = trackingUrl
    ? ((status === 'LIVE' && VANITY_ENABLED) ? `https://${trackingUrl}` : `${TRACKING_APP_URL}/?host=${encodeURIComponent(trackingUrl)}`)
    : null;
  // Client-side brand/URL/status filter for the tracking-pages table.
  const q = listSearch.trim().toLowerCase();
  const filteredList = q
    ? list.filter((row) =>
        (row.brandName || '').toLowerCase().includes(q)
        || (row.trackingUrl || '').toLowerCase().includes(q)
        || (row.status || '').toLowerCase().includes(q))
    : list;

  const logicalW = device === 'mobile' ? MOBILE_W : DESKTOP_W;
  const frameH = device === 'mobile' ? 720 : 760;
  const scale = wrapW ? Math.min(1, wrapW / logicalW) : (device === 'mobile' ? 1 : 0.6);

  return (
    <Box sx={{ p: 2, fontFamily: FONT }}>
      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: '1.45rem', color: NAVY, mb: 0.5 }}>
        DIY Tracking Page
      </Typography>
      <Typography sx={{ ...hintSx, mb: 2.5 }}>
        Design the branded page customers see when tracking their orders
      </Typography>

      <Box sx={sectionCardSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography sx={cardTitleSx}>Tracking pages</Typography>
          {list.length > 0 && (
            <TextField
              size="small"
              placeholder="Search brand / URL / status…"
              value={listSearch}
              onChange={(e) => { setListSearch(e.target.value); setListPage(0); }}
              sx={{ width: { xs: '100%', sm: 280 } }}
            />
          )}
        </Box>
        {list.length === 0 ? (
          <Typography sx={hintSx}>No tracking pages configured yet.</Typography>
        ) : filteredList.length === 0 ? (
          <Typography sx={hintSx}>No tracking pages match “{listSearch.trim()}”.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Brand</TableCell>
                  <TableCell>Tracking URL</TableCell>
                  <TableCell>Design</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredList.slice(listPage * LIST_ROWS_PER_PAGE, (listPage + 1) * LIST_ROWS_PER_PAGE).map((row) => (
                  <TableRow key={row.merchantOid} hover>
                    <TableCell>{row.brandName || '—'}</TableCell>
                    <TableCell>{row.trackingUrl || '—'}</TableCell>
                    <TableCell>{row.designTemplate || '—'}</TableCell>
                    <TableCell>
                      {row.status ? (
                        <Chip size="small" label={row.status} color={row.status === 'LIVE' ? 'success' : row.status === 'DISABLED' ? 'warning' : 'default'} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        sx={secondaryBtnSx}
                        onClick={() => {
                          setManagedOid(row.merchantOid);
                          setValue('merchant', row.merchantOid);
                        }}
                      >
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {filteredList.length > LIST_ROWS_PER_PAGE && (
              <TablePagination
                component="div"
                count={filteredList.length}
                page={listPage}
                onPageChange={(_e, newPage) => setListPage(newPage)}
                rowsPerPage={LIST_ROWS_PER_PAGE}
                rowsPerPageOptions={[LIST_ROWS_PER_PAGE]}
              />
            )}
          </TableContainer>
        )}
      </Box>

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Box sx={sectionCardSx}>
          <Typography sx={{ ...cardTitleSx, mb: 1.5 }}>Select a merchant</Typography>
          <MerchantFilterCached required />
        </Box>

        {merchantOid && (
          <>
            <Box sx={sectionCardSx}>
              <Typography sx={{ ...cardTitleSx, mb: 1 }}>Tracking URL</Typography>
              {trackingUrl && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography component="a" href={publicTrackingUrl} target="_blank" rel="noreferrer"
                      sx={{ fontFamily: FONT, fontSize: '0.9rem', fontWeight: 600, color: PRIMARY_BLUE, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      {publicTrackingUrl}
                    </Typography>
                    {status && (
                      <Chip
                        size="small"
                        label={status}
                        color={status === 'LIVE' ? 'success' : status === 'DISABLED' ? 'warning' : status === 'FAILED' ? 'error' : 'default'}
                      />
                    )}
                    {status === 'LIVE' && (
                      <Button size="small" variant="outlined" color="error" disabled={togglingLive} onClick={() => onToggleLive(false)}
                        sx={{ textTransform: 'none', fontFamily: FONT, fontSize: 12, py: 0.25, px: 1.25, borderRadius: '6px', lineHeight: 1.6 }}>
                        Take page down
                      </Button>
                    )}
                    {status === 'DISABLED' && (
                      <Button size="small" variant="outlined" color="success" disabled={togglingLive} onClick={() => onToggleLive(true)}
                        sx={{ textTransform: 'none', fontFamily: FONT, fontSize: 12, py: 0.25, px: 1.25, borderRadius: '6px', lineHeight: 1.6 }}>
                        Publish again
                      </Button>
                    )}
                  </Box>
                  {status === 'FAILED' && statusReason && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {statusReason}
                    </Typography>
                  )}
                  {status === 'PROVISIONING' && (
                    <Typography sx={{ ...hintSx, display: 'block', mt: 0.5 }}>
                      Stale status from the old provisioning flow — click “Assign URL” again to mark it live.
                    </Typography>
                  )}
                </Box>
              )}
              {/* flex-start + fixed-height button wrapper: centers the button against the INPUT
                  box (40px), not against input+helper-text (which made it sit visibly lower). */}
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Tracking URL (e.g. mybrand.proship.in)"
                    value={assignInput}
                    onChange={(e) => setAssignInput(e.target.value)}
                    helperText="Pre-filled from the brand website — edit if needed, then assign."
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                  <LoadingButton type="button" variant="contained" loading={assigning} onClick={onAssignUrl} sx={primaryBtnSx}>
                    Assign URL
                  </LoadingButton>
                </Grid>
              </Grid>
            </Box>

            <Box sx={sectionCardSx}>
              <Box sx={sectionHeaderSx}>
                <Box sx={numBadgeSx(PRIMARY_BLUE)}>1</Box>
                <Typography sx={cardTitleSx}>Brand identity</Typography>
                <Typography component="span" sx={hintSx}>* required</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <RHFTextField name="brandName" label="Brand name *" />
                </Grid>
                <Grid item xs={12} md={4}>
                  <RHFTextField name="webUrl" label="Brand website URL" placeholder="https://yourbrand.com" />
                </Grid>
                <Grid item xs={12} md={4}>
                  <RHFTextField name="bannerBgColor" label="Accent / banner colour" placeholder="#1D9E75" />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <UploadedThumb label="Current logo" url={logoUrl} height={36} onRemove={() => { setLogoUrl(''); setValue('file', null); }} />
                  {/* Raster only: SVG needs an image/svg+xml Content-Type to render in <img>, which the upload
                      endpoint doesn't set — PNG/JPG are content-sniffed by browsers and always render. */}
                  <RHFFileField name="file" label="Brand logo" fileType={['.png', '.jpg', '.jpeg']}
                    helperText="Recommended 360×120px or larger — transparent PNG looks best" />
                  {uploading.logo && <Typography sx={hintSx}>Uploading logo…</Typography>}
                </Grid>
                <Grid item xs={12} md={4}>
                  <UploadedThumb label="Current desktop banner" url={bannerLargeUrl} height={36} onRemove={() => { setBannerLargeUrl(''); setValue('bannerDesktopFile', null); }} />
                  <RHFFileField name="bannerDesktopFile" label="Banner — desktop" fileType={['.png', '.jpg', '.jpeg']}
                    helperText="Recommended 1920×320px (6:1 strip)" />
                  {uploading.large && <Typography sx={hintSx}>Uploading…</Typography>}
                </Grid>
                <Grid item xs={12} md={4}>
                  <UploadedThumb label="Current mobile banner" url={bannerSmallUrl} height={36} onRemove={() => { setBannerSmallUrl(''); setValue('bannerMobileFile', null); }} />
                  <RHFFileField name="bannerMobileFile" label="Banner — mobile" fileType={['.png', '.jpg', '.jpeg']}
                    helperText="Recommended 750×375px (2:1)" />
                  {uploading.small && <Typography sx={hintSx}>Uploading…</Typography>}
                </Grid>
              </Grid>
            </Box>

            <Box sx={sectionCardSx}>
              <Box sx={sectionHeaderSx}>
                <Box sx={numBadgeSx('#283593')}>2</Box>
                <Typography sx={cardTitleSx}>Footer &amp; links</Typography>
                <Box component="span" sx={optionalTagSx}>Optional</Box>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <RHFTextArea name="footer" label="Footer note" rows={2} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFTextField name="whatsappNumber" label="Support WhatsApp number" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFTextField name="instaUrl" label="Instagram URL" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFTextField name="fbUrl" label="Facebook URL" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFTextField name="twitterUrl" label="X / Twitter URL" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFTextField name="linkedinUrl" label="LinkedIn URL" />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <RHFTextField name="termsAndConditions" label="Terms &amp; Conditions URL" />
                </Grid>
                <Grid item xs={12} md={4}>
                  <RHFTextField name="returnPolicy" label="Return Policy URL" />
                </Grid>
                <Grid item xs={12} md={4}>
                  <RHFTextField name="privacyPolicy" label="Privacy Policy URL" />
                </Grid>
              </Grid>
            </Box>

            <Box sx={sectionCardSx}>
              <Box sx={{ ...sectionHeaderSx, mb: 0.5 }}>
                <Box sx={numBadgeSx('#E65100')}>3</Box>
                <Typography sx={cardTitleSx}>Choose a design &amp; preview</Typography>
                <Box component="span" sx={requiredTagSx}>Required</Box>
              </Box>
              <Typography sx={{ ...hintSx, display: 'block', mb: 1.5 }}>
                Pick a design — the preview below updates instantly so you can compare them.
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {TEMPLATE_CARDS.map((t) => {
                  const selected = designTemplate === t.value;
                  return (
                    <Grid item xs={12} sm={4} key={t.value}>
                      <Box
                        onClick={() => setValue('designTemplate', t.value, { shouldDirty: true })}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: '#fff',
                          border: selected ? `2px solid ${PRIMARY_BLUE}` : `1px solid ${BORDERC}`,
                          borderRadius: '10px',
                          boxShadow: selected ? SHADOW_HOVER : SHADOW_CARD,
                          transition: 'all 0.2s',
                          '&:hover': { transform: 'scale(1.01)', boxShadow: SHADOW_HOVER },
                          p: 1.5,
                          height: '100%',
                        }}
                      >
                        <Box sx={{ height: 80, bgcolor: 'grey.50', borderRadius: 1, p: 1, mb: 1, overflow: 'hidden' }}>
                          {t.value === 'CLASSIC' && (
                            <Box>
                              <Box sx={{ height: 12, bgcolor: 'primary.main', borderRadius: 0.5, mb: 0.75 }} />
                              {[0, 1, 2].map((i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'grey.400' }} />
                                  <Box sx={{ flex: 1, height: 5, bgcolor: 'grey.300', borderRadius: 0.5 }} />
                                </Box>
                              ))}
                            </Box>
                          )}
                          {t.value === 'MODERN' && (
                            <Box>
                              <Box sx={{ width: '55%', height: 8, bgcolor: 'primary.main', borderRadius: 0.5, mb: 1 }} />
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                                {[0, 1, 2, 3].map((i) => (
                                  <React.Fragment key={i}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: i <= 1 ? 'primary.main' : 'grey.300' }} />
                                    {i < 3 && <Box sx={{ flex: 1, height: 2, bgcolor: i < 1 ? 'primary.main' : 'grey.300' }} />}
                                  </React.Fragment>
                                ))}
                              </Box>
                              {[0, 1].map((i) => (
                                <Box key={i} sx={{ height: 5, bgcolor: 'grey.300', borderRadius: 0.5, mb: 0.5 }} />
                              ))}
                            </Box>
                          )}
                          {t.value === 'MINIMAL' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 0.75 }}>
                              <Box sx={{ width: '40%', height: 6, bgcolor: 'grey.400', borderRadius: 0.5 }} />
                              <Box sx={{ width: '70%', height: 5, bgcolor: 'grey.300', borderRadius: 0.5 }} />
                              <Box sx={{ width: '55%', height: 5, bgcolor: 'grey.300', borderRadius: 0.5 }} />
                            </Box>
                          )}
                        </Box>
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', fontWeight: 700, color: NAVY }}>{t.label}</Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', fontWeight: 700, color: NAVY }}>Preview</Typography>
                  <Chip
                    label="Desktop" size="small"
                    color={device === 'desktop' ? 'primary' : 'default'}
                    variant={device === 'desktop' ? 'filled' : 'outlined'}
                    onClick={() => setDevice('desktop')}
                  />
                  <Chip
                    label="Mobile" size="small"
                    color={device === 'mobile' ? 'primary' : 'default'}
                    variant={device === 'mobile' ? 'filled' : 'outlined'}
                    onClick={() => setDevice('mobile')}
                  />
                </Stack>
                <Button size="small" sx={secondaryBtnSx} onClick={() => setPreviewSrc(buildPreviewUrl())}>
                  Refresh preview
                </Button>
              </Box>
              <Box
                ref={previewWrapRef}
                sx={{ border: `1px solid ${BORDERC}`, borderRadius: '10px', overflow: 'hidden', bgcolor: 'grey.100', p: 1 }}
              >
                <Box sx={{ width: logicalW * scale, height: frameH * scale, mx: device === 'mobile' ? 'auto' : 0, position: 'relative' }}>
                  <iframe
                    title="Tracking page preview"
                    src={previewSrc}
                    style={{
                      width: logicalW,
                      height: frameH,
                      border: 0,
                      display: 'block',
                      transform: `scale(${scale})`,
                      transformOrigin: 'top left',
                    }}
                  />
                </Box>
              </Box>
              <Typography sx={{ ...hintSx, display: 'block', mt: 1 }}>
                Live sample with this branding and the selected design. Text edits (name, colour, footer)
                appear after you click “Refresh preview”.
              </Typography>
            </Box>

            <LoadingButton type="submit" variant="contained" loading={loading} disabled={anyUploading} sx={primaryBtnSx}>
              Save branding
            </LoadingButton>
          </>
        )}
      <ImageCropDialog
        open={!!pendingCrop}
        file={pendingCrop ? pendingCrop.file : null}
        aspect={pendingCrop ? SLOTS[pendingCrop.slot].aspect : undefined}
        outWidth={pendingCrop ? SLOTS[pendingCrop.slot].outWidth : undefined}
        minWidth={pendingCrop ? SLOTS[pendingCrop.slot].minWidth : 0}
        minHeight={pendingCrop ? SLOTS[pendingCrop.slot].minHeight : 0}
        title={pendingCrop ? SLOTS[pendingCrop.slot].title : ''}
        hint={pendingCrop ? SLOTS[pendingCrop.slot].hint : ''}
        onCancel={closeCrop}
        onConfirm={onCropped}
      />
      </FormProvider>
    </Box>
  );
}
